import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function InternetfuehrerscheinManager() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [examSlots, setExamSlots] = useState([]);
  const [examBookings, setExamBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('questions');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [message, setMessage] = useState(null);

  // New exam slot form
  const [newSlot, setNewSlot] = useState({
    certificate_id: '',
    date: '',
    time: '14:00',
    duration: 30,
    max_participants: 3
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, certRes, slotsRes, bookingsRes] = await Promise.all([
        fetch('/api/admin/quiz/categories', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        fetch('/api/certificates', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        fetch('/api/admin/exams/slots', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        fetch('/api/admin/exams/bookings', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        })
      ]);

      setCategories(await catRes.json());
      setCertificates(await certRes.json());
      setExamSlots(await slotsRes.json());
      setExamBookings(await bookingsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const fetchQuestions = async (categoryId) => {
    try {
      const res = await fetch(`/api/admin/quiz/categories/${categoryId}/questions`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setQuestions(await res.json());
    } catch (err) {
      console.error('Error fetching questions:', err);
    }
  };

  const selectCategory = (cat) => {
    setSelectedCategory(cat);
    fetchQuestions(cat.id);
    setEditingQuestion(null);
  };

  const saveQuestion = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/quiz/questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          ...editingQuestion,
          category_id: selectedCategory.id
        })
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Frage gespeichert!' });
        fetchQuestions(selectedCategory.id);
        setEditingQuestion(null);
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Speichern' });
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Frage wirklich loeschen?')) return;
    try {
      await fetch(`/api/admin/quiz/questions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      fetchQuestions(selectedCategory.id);
      fetchData();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Loeschen' });
    }
  };

  const createExamSlot = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/exams/slots', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(newSlot)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Pruefungstermin erstellt!' });
        setNewSlot({ certificate_id: '', date: '', time: '14:00', duration: 30, max_participants: 3 });
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Erstellen' });
    }
  };

  const deleteExamSlot = async (id) => {
    if (!window.confirm('Termin wirklich loeschen?')) return;
    try {
      await fetch(`/api/admin/exams/slots/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      fetchData();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Loeschen' });
    }
  };

  const recordResult = async (bookingId, passed) => {
    const score = passed ? 100 : 0;
    const notes = prompt('Anmerkungen (optional):') || '';

    try {
      const res = await fetch('/api/admin/exams/results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          exam_booking_id: bookingId,
          passed,
          score,
          trainer_notes: notes
        })
      });

      if (res.ok) {
        setMessage({ type: 'success', text: passed ? 'Bestanden! Zertifikat erstellt.' : 'Nicht bestanden eingetragen.' });
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Speichern' });
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border"></div></div>;
  }

  return (
    <div>
      <h4 className="mb-4">Internetfuehrerschein</h4>

      {message && (
        <div className={`alert alert-${message.type} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'questions' ? 'active' : ''}`} onClick={() => setActiveTab('questions')}>
            Quiz-Fragen
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'exams' ? 'active' : ''}`} onClick={() => setActiveTab('exams')}>
            Pruefungstermine
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>
            Pruefungen ({examBookings.filter(b => b.status === 'confirmed').length})
          </button>
        </li>
      </ul>

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="row">
          <div className="col-md-4">
            <div className="card">
              <div className="card-header">Kategorien</div>
              <div className="list-group list-group-flush">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedCategory?.id === cat.id ? 'active' : ''}`}
                    onClick={() => selectCategory(cat)}
                  >
                    <span>{cat.emoji} {cat.name}</span>
                    <span className="badge bg-secondary">{cat.question_count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-8">
            {selectedCategory ? (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>{selectedCategory.emoji} {selectedCategory.name}</span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setEditingQuestion({
                      question: '',
                      answers: ['', '', '', ''],
                      correct_answer: 0,
                      explanation: '',
                      sort_order: questions.length
                    })}
                  >
                    + Neue Frage
                  </button>
                </div>
                <div className="card-body">
                  {editingQuestion ? (
                    <form onSubmit={saveQuestion}>
                      <div className="mb-3">
                        <label className="form-label">Frage</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={editingQuestion.question}
                          onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Antworten</label>
                        {editingQuestion.answers.map((ans, idx) => (
                          <div key={idx} className="input-group mb-2">
                            <div className="input-group-text">
                              <input
                                type="radio"
                                name="correct"
                                checked={editingQuestion.correct_answer === idx}
                                onChange={() => setEditingQuestion({ ...editingQuestion, correct_answer: idx })}
                              />
                            </div>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={`Antwort ${idx + 1}`}
                              value={ans}
                              onChange={(e) => {
                                const newAnswers = [...editingQuestion.answers];
                                newAnswers[idx] = e.target.value;
                                setEditingQuestion({ ...editingQuestion, answers: newAnswers });
                              }}
                              required
                            />
                          </div>
                        ))}
                        <small className="text-muted">Waehle die richtige Antwort mit dem Radio-Button</small>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Erklaerung (optional)</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={editingQuestion.explanation || ''}
                          onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation: e.target.value })}
                          placeholder="Wird nach der Antwort angezeigt"
                        />
                      </div>
                      <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-success">Speichern</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setEditingQuestion(null)}>Abbrechen</button>
                      </div>
                    </form>
                  ) : questions.length === 0 ? (
                    <p className="text-muted">Noch keine Fragen vorhanden.</p>
                  ) : (
                    <div className="list-group">
                      {questions.map((q, idx) => (
                        <div key={q.id} className="list-group-item">
                          <div className="d-flex justify-content-between">
                            <strong>Frage {idx + 1}</strong>
                            <div>
                              <button
                                className="btn btn-outline-primary btn-sm me-1"
                                onClick={() => setEditingQuestion({
                                  ...q,
                                  answers: typeof q.answers === 'string' ? JSON.parse(q.answers) : q.answers
                                })}
                              >
                                Bearbeiten
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => deleteQuestion(q.id)}
                              >
                                Loeschen
                              </button>
                            </div>
                          </div>
                          <p className="mb-1">{q.question}</p>
                          <small className="text-muted">
                            Richtige Antwort: {(typeof q.answers === 'string' ? JSON.parse(q.answers) : q.answers)[q.correct_answer]}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="alert alert-info">Waehle eine Kategorie aus</div>
            )}
          </div>
        </div>
      )}

      {/* Exams Tab */}
      {activeTab === 'exams' && (
        <div className="row">
          <div className="col-md-5">
            <div className="card">
              <div className="card-header">Neuer Pruefungstermin</div>
              <div className="card-body">
                <form onSubmit={createExamSlot}>
                  <div className="mb-3">
                    <label className="form-label">Fuehrerschein</label>
                    <select
                      className="form-select"
                      value={newSlot.certificate_id}
                      onChange={(e) => setNewSlot({ ...newSlot, certificate_id: e.target.value })}
                      required
                    >
                      <option value="">Bitte waehlen...</option>
                      {certificates.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <label className="form-label">Datum</label>
                      <input
                        type="date"
                        className="form-control"
                        value={newSlot.date}
                        onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-6 mb-3">
                      <label className="form-label">Uhrzeit</label>
                      <input
                        type="time"
                        className="form-control"
                        value={newSlot.time}
                        onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <label className="form-label">Dauer (Min)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={newSlot.duration}
                        onChange={(e) => setNewSlot({ ...newSlot, duration: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="col-6 mb-3">
                      <label className="form-label">Max. Teilnehmer</label>
                      <input
                        type="number"
                        className="form-control"
                        value={newSlot.max_participants}
                        onChange={(e) => setNewSlot({ ...newSlot, max_participants: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary w-100">Termin erstellen</button>
                </form>
              </div>
            </div>
          </div>
          <div className="col-md-7">
            <div className="card">
              <div className="card-header">Pruefungstermine</div>
              <div className="card-body p-0">
                {examSlots.length === 0 ? (
                  <p className="text-muted p-3 mb-0">Keine Termine vorhanden</p>
                ) : (
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Uhrzeit</th>
                        <th>Typ</th>
                        <th>Buchungen</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {examSlots.map(slot => (
                        <tr key={slot.id}>
                          <td>{new Date(slot.date).toLocaleDateString('de-DE')}</td>
                          <td>{slot.time}</td>
                          <td>
                            <span className={`badge ${slot.certificate_id === 1 ? 'bg-primary' : 'bg-warning text-dark'}`}>
                              {slot.certificate_name}
                            </span>
                          </td>
                          <td>{slot.booked_count}/{slot.max_participants}</td>
                          <td>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => deleteExamSlot(slot.id)}
                              disabled={slot.booked_count > 0}
                            >
                              Loeschen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="card">
          <div className="card-header">Angemeldete Pruefungen</div>
          <div className="card-body p-0">
            {examBookings.length === 0 ? (
              <p className="text-muted p-3 mb-0">Keine Anmeldungen vorhanden</p>
            ) : (
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Teilnehmer</th>
                    <th>Fuehrerschein</th>
                    <th>Status</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {examBookings.map(booking => (
                    <tr key={booking.id}>
                      <td>
                        {new Date(booking.date).toLocaleDateString('de-DE')}, {booking.time}
                      </td>
                      <td>
                        <strong>{booking.child_name}</strong>
                        <br />
                        <small className="text-muted">{booking.first_name} {booking.last_name}</small>
                      </td>
                      <td>
                        <span className={`badge ${booking.certificate_name.includes('Klein') ? 'bg-primary' : 'bg-warning text-dark'}`}>
                          {booking.certificate_name}
                        </span>
                      </td>
                      <td>
                        {booking.status === 'confirmed' && <span className="badge bg-info">Angemeldet</span>}
                        {booking.status === 'passed' && <span className="badge bg-success">Bestanden</span>}
                        {booking.status === 'failed' && <span className="badge bg-danger">Nicht bestanden</span>}
                        {booking.status === 'cancelled' && <span className="badge bg-secondary">Storniert</span>}
                      </td>
                      <td>
                        {booking.status === 'confirmed' && (
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-success"
                              onClick={() => recordResult(booking.id, true)}
                            >
                              Bestanden
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => recordResult(booking.id, false)}
                            >
                              Nicht bestanden
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InternetfuehrerscheinManager;
